// UnitManager.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Container, Typography, TextField, Button, Paper, Stack, IconButton,
  Dialog, DialogTitle, DialogActions, Chip, FormControl, InputLabel,
  MenuItem, Select, FormControlLabel, Switch, Tooltip, Divider, Box
} from '@mui/material';
import { db } from './firebase';
import {
  collection, setDoc, getDocs, deleteDoc, doc, serverTimestamp, query, orderBy
} from 'firebase/firestore';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import SearchIcon from '@mui/icons-material/Search';

const BASE_DIMENSIONS = ['mass', 'volume', 'count'];
const KINDS = ['standard_unit', 'scoop', 'disher', 'pump', 'spoon', 'cup', 'other'];

const normalizeId = (s) =>
  (s || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const UnitManager = () => {
  const [units, setUnits] = useState([]);
  const [newUnit, setNewUnit] = useState({
    id: '',
    label: '',
    kind: 'standard_unit',
    base_dimension: '',
    active: true
  });
  const [editingUnit, setEditingUnit] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Search + status filter
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'all' | 'active' | 'archived'

  // Fetch and SORT (A→Z by label). If index missing, fall back to client sort.
  const fetchUnits = async () => {
    try {
      const q = query(collection(db, 'ingredient_units'), orderBy('label'));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setUnits(list);
    } catch {
      const snapshot = await getDocs(collection(db, 'ingredient_units'));
      const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
      setUnits(list);
    }
  };

  const resetNew = () => setNewUnit({
    id: '',
    label: '',
    kind: 'standard_unit',
    base_dimension: '',
    active: true
  });

  const canAdd = () => newUnit.id && newUnit.label && newUnit.base_dimension;

  const handleAdd = async () => {
    const id = normalizeId(newUnit.id);
    if (!id || !canAdd()) return;
    await setDoc(doc(db, 'ingredient_units', id), {
      label: newUnit.label.trim(),
      kind: newUnit.kind,
      base_dimension: newUnit.base_dimension,
      active: !!newUnit.active,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp()
    });
    resetNew();
    fetchUnits();
  };

  const handleEditStart = (unit) => setEditingUnit({ ...unit });
  const handleEditCancel = () => setEditingUnit(null);

  const handleEditSave = async () => {
    if (!editingUnit?.label || !editingUnit?.base_dimension) return;
    await setDoc(doc(db, 'ingredient_units', editingUnit.id), {
      label: editingUnit.label.trim(),
      kind: editingUnit.kind || 'standard_unit',
      base_dimension: editingUnit.base_dimension,
      active: !!editingUnit.active,
      updated_at: serverTimestamp()
    }, { merge: true });
    setEditingUnit(null);
    fetchUnits();
  };

  // Archive/restore via active flag
  const handleArchiveToggle = async (unit, nextActive) => {
    await setDoc(doc(db, 'ingredient_units', unit.id), {
      active: !!nextActive,
      updated_at: serverTimestamp()
    }, { merge: true });
    fetchUnits();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    // TODO: Prevent deletion if referenced by any ingredient calibration.
    await deleteDoc(doc(db, 'ingredient_units', deleteTarget.id));
    setDeleteTarget(null);
    fetchUnits();
  };

  useEffect(() => { fetchUnits(); }, []);

  // --- SEARCH + FILTER LOGIC ---
  const matchesSearch = (u, term) => {
    if (!term) return true;
    const t = term.toLowerCase();
    return (
      (u.id || '').toLowerCase().includes(t) ||
      (u.label || '').toLowerCase().includes(t) ||
      (u.kind || '').toLowerCase().includes(t) ||
      (u.base_dimension || '').toLowerCase().includes(t)
    );
  };

  const statusPass = (u) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'active') return !!u.active;
    if (statusFilter === 'archived') return !u.active;
    return true;
  };

  const filteredUnits = useMemo(
    () => units.filter(u => statusPass(u) && matchesSearch(u, search)),
    [units, statusFilter, search]
  );

  const activeCount = units.filter(u => !!u.active).length;
  const archivedCount = units.filter(u => !u.active).length;

  return (
    <Container maxWidth="md">
      <Typography variant="h5" gutterBottom>🧪 Unit Manager</Typography>

      {/* Create / Add */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Stack spacing={2}>
          <Typography variant="subtitle1">Add New Unit</Typography>
          <TextField
            label="Unit ID (e.g. g, ml, oz, fl_oz, scoop_medium)"
            value={newUnit.id}
            onChange={(e) => setNewUnit({ ...newUnit, id: normalizeId(e.target.value) })}
            helperText="Lowercase letters, numbers, underscores"
            fullWidth
          />
          <TextField
            label="Label (e.g. Ounce (oz))"
            value={newUnit.label}
            onChange={(e) => setNewUnit({ ...newUnit, label: e.target.value })}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Base Dimension</InputLabel>
            <Select
              label="Base Dimension"
              value={newUnit.base_dimension}
              onChange={(e) => setNewUnit({ ...newUnit, base_dimension: e.target.value })}
            >
              {BASE_DIMENSIONS.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Kind</InputLabel>
            <Select
              label="Kind"
              value={newUnit.kind}
              onChange={(e) => setNewUnit({ ...newUnit, kind: e.target.value })}
            >
              {KINDS.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={!!newUnit.active} onChange={(e) => setNewUnit({ ...newUnit, active: e.target.checked })} />}
            label="Active"
          />
          <Button variant="contained" onClick={handleAdd} disabled={!canAdd()}>Add Unit</Button>
        </Stack>
      </Paper>

      {/* Search + Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <SearchIcon fontSize="small" />
            <TextField
              label="Search units (id, label, kind, base)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              placeholder="e.g. oz, scoop, volume…"
            />
          </Box>
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="active">Active ({activeCount})</MenuItem>
              <MenuItem value="archived">Archived ({archivedCount})</MenuItem>
              <MenuItem value="all">All ({units.length})</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <Typography variant="h6" gutterBottom>
        Units {statusFilter !== 'all' ? `— ${statusFilter[0].toUpperCase() + statusFilter.slice(1)}` : ''}
      </Typography>

      <Stack spacing={2}>
        {filteredUnits.length === 0 && (
          <Paper sx={{ p: 2 }}><Typography variant="body2">No units match your filters.</Typography></Paper>
        )}
        {filteredUnits.map((unit) => (
          <Paper
            key={unit.id}
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap'
            }}
          >
            {editingUnit?.id === unit.id ? (
              <>
                <Stack spacing={1} sx={{ flex: 1, minWidth: 280 }}>
                  <TextField
                    label="Label"
                    value={editingUnit.label || ''}
                    onChange={(e) => setEditingUnit({ ...editingUnit, label: e.target.value })}
                  />
                  <FormControl>
                    <InputLabel>Base Dimension</InputLabel>
                    <Select
                      label="Base Dimension"
                      value={editingUnit.base_dimension || ''}
                      onChange={(e) => setEditingUnit({ ...editingUnit, base_dimension: e.target.value })}
                    >
                      {BASE_DIMENSIONS.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <InputLabel>Kind</InputLabel>
                    <Select
                      label="Kind"
                      value={editingUnit.kind || 'standard_unit'}
                      onChange={(e) => setEditingUnit({ ...editingUnit, kind: e.target.value })}
                    >
                      {KINDS.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={<Switch checked={!!editingUnit.active} onChange={(e) => setEditingUnit({ ...editingUnit, active: e.target.checked })} />}
                    label="Active"
                  />
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Save">
                    <IconButton onClick={handleEditSave}><SaveIcon /></IconButton>
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <IconButton onClick={handleEditCancel}><CancelIcon /></IconButton>
                  </Tooltip>
                </Stack>
              </>
            ) : (
              <>
                <Stack sx={{ flex: 1, minWidth: 280 }}>
                  <Typography variant="subtitle1">
                    {unit.label} <Typography component="span" variant="body2">({unit.id})</Typography>
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                    <Chip size="small" label={`base: ${unit.base_dimension || '-'}`} />
                    <Chip size="small" label={`kind: ${unit.kind || 'standard_unit'}`} />
                    {!unit.active && <Chip size="small" color="warning" label="archived" />}
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Edit">
                    <IconButton onClick={() => handleEditStart(unit)}><EditIcon /></IconButton>
                  </Tooltip>
                  {unit.active ? (
                    <Tooltip title="Archive">
                      <IconButton onClick={() => handleArchiveToggle(unit, false)}><ArchiveIcon /></IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Restore">
                      <IconButton onClick={() => handleArchiveToggle(unit, true)}><UnarchiveIcon /></IconButton>
                    </Tooltip>
                  )}
                  <Divider flexItem orientation="vertical" />
                  <Tooltip title="Delete (permanent)">
                    <IconButton onClick={() => setDeleteTarget(unit)}><DeleteIcon /></IconButton>
                  </Tooltip>
                </Stack>
              </>
            )}
          </Paper>
        ))}
      </Stack>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete “{deleteTarget?.label}” permanently?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UnitManager;
